import { useState } from 'react';
import { Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff } from 'lucide-react';
import Logo from '../../images/co_logo.png';

// Shared shell for the public auth screens (login / register / verify / forgot / reset).
export default function AuthCard({ title, subtitle, children }) {
	return (
		<div className='min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 flex items-center justify-center px-4'>
			<div className='w-full max-w-md bg-white p-8 border rounded-2xl'>
				<div className='text-center pb-6'>
					<div className='mx-auto mb-4 bg-white rounded-full flex items-center justify-center'>
						<img src={Logo} alt='PI Logo' className='w-auto h-[40px]' />
					</div>
					<h1 className='text-2xl font-semibold'>{title}</h1>
					{subtitle && <p className='text-sm text-gray-600 mt-1'>{subtitle}</p>}
				</div>
				<div className='space-y-6'>{children}</div>
			</div>
		</div>
	);
}

export const FormError = ({ message }) =>
	message ? (
		<div className='border border-pi-red/50 rounded-lg p-3 text-sm text-pi-red'>{message}</div>
	) : null;

export const FormSuccess = ({ message }) =>
	message ? (
		<div className='border border-green-600/50 rounded-lg p-3 text-sm text-green-700'>{message}</div>
	) : null;

export const TextField = ({ name, label, ...props }) => (
	<div className='space-y-2'>
		<label htmlFor={name} className='text-sm font-medium text-gray-700'>
			{label}
		</label>
		<Field id={name} name={name} className='form-input' {...props} />
		<ErrorMessage name={name} component='p' className='error' />
	</div>
);

export const PasswordField = ({ name, label, hint, autoComplete = 'new-password' }) => {
	const [show, setShow] = useState(false);

	return (
		<div className='space-y-2'>
			<label htmlFor={name} className='text-sm font-medium text-gray-700'>
				{label}
			</label>
			<div className='relative'>
				<Field
					id={name}
					name={name}
					type={show ? 'text' : 'password'}
					placeholder='••••••••'
					autoComplete={autoComplete}
					className='form-input pr-10'
				/>
				<button
					type='button'
					className='absolute inset-y-0 right-0 pr-3 flex items-center'
					onClick={() => setShow(!show)}
					aria-label={show ? 'Hide password' : 'Show password'}
				>
					{show ? (
						<EyeOff className='h-4 w-4 text-gray-400' />
					) : (
						<Eye className='h-4 w-4 text-gray-400' />
					)}
				</button>
			</div>
			{hint && <p className='text-xs text-gray-500'>{hint}</p>}
			<ErrorMessage name={name} component='p' className='error' />
		</div>
	);
};

export const PASSWORD_HINT = 'At least 8 characters, including a letter and a number.';

// Mirrors the API's password rules (adminAccount.service.ts assertPassword).
export const newPasswordSchema = {
	password: Yup.string()
		.min(8, 'Password must be at least 8 characters.')
		.max(128, 'Password must be 128 characters or fewer.')
		.matches(/[A-Za-z]/, 'Password must include a letter.')
		.matches(/\d/, 'Password must include a number.')
		.required('Password is required.'),
	confirmPassword: Yup.string()
		.oneOf([Yup.ref('password')], 'Passwords do not match.')
		.required('Please confirm your password.'),
};
